/**
 * "Insights" card (Make Today real phase 2, 2026-09-01 —
 * docs/plans/2026-09-01-make-today-real-phase2.md). Up to 3 rule-based,
 * dollar-ranked nudges from GET /api/dashboard's `insights` — no AI, server
 * assembles them. Same nullable-section idiom as MarginFlowCard: `undefined`
 * is the dashboard query still loading (skeleton); `null` means neither
 * rule-owning module (Amazon, warehouse) is enabled for the tenant; an
 * empty array means the modules are on but nothing currently fires
 * ("Nothing urgent" empty state, no badge).
 */
import { Link } from '@tanstack/react-router';
import { money } from '../../lib/price-calibration';
import { Skeleton } from '../../components/Skeleton';
import type { InsightItem } from '@sourcito/schemas';

export interface InsightsCardProps {
  insights: InsightItem[] | null | undefined;
}

export function InsightsCard({ insights }: InsightsCardProps) {
  return (
    <div className="bg-surface border border-border-subtle rounded-xl p-5">
      <h3 className="text-sm font-medium text-fg-primary">Insights</h3>
      <p className="text-xs text-fg-tertiary mt-1 leading-relaxed">
        Ranked, dollar-valued nudges — file this claim, reorder that SKU.
      </p>

      {insights === undefined && (
        <div className="mt-4 space-y-2" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-10 rounded-lg" />
          ))}
        </div>
      )}

      {insights === null && (
        <p className="text-sm text-fg-tertiary mt-4">
          Connect the Amazon or Warehouse module to see insights.
        </p>
      )}

      {insights != null && insights.length === 0 && (
        <p className="text-sm text-fg-tertiary mt-4">Nothing urgent — the machine is quiet.</p>
      )}

      {insights != null && insights.length > 0 && (
        <ul className="mt-4 space-y-1">
          {insights.map((item, i) => (
            <li key={`${item.kind}:${i}`}>
              <Link
                to={item.href}
                className="flex items-center gap-2 py-1.5 px-1 -mx-1 rounded-md group hover:bg-surface-elev transition-colors duration-fast"
              >
                <span className="text-sm text-fg-primary flex-1 min-w-0">{item.label}</span>
                {item.valueCents != null && (
                  <span className="font-display text-sm text-fg-primary shrink-0">
                    {money(item.valueCents)}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
